import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import PublicPageLayout from "@/components/PublicPageLayout";
import Seo, { pageSchema } from "@/components/Seo";
import { WhatsAppButton } from "@/components/WhatsAppFab";
import { Button } from "@/components/ui/button";
import { WA_MESSAGES, PRICING } from "@/lib/site";

function Tier({ label, price, period, features, wa, highlighted, testid }) {
  return (
    <div className={`rounded-xl border ${highlighted ? "border-primary bg-primary/5" : "border-border bg-card"} p-6`} data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="text-3xl font-heading font-black num">{price}</span>
        <span className="text-sm text-muted-foreground">/ {period}</span>
      </div>
      <ul className="mt-5 space-y-2 text-sm">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-primary mt-0.5" /> {f}</li>
        ))}
      </ul>
      <div className="mt-6"><WhatsAppButton message={wa} label="Contact on WhatsApp" className="w-full justify-center" testid={`${testid}-wa`} /></div>
    </div>
  );
}

export default function Pricing() {
  const path = "/pricing";
  const title = "MCA & GST Database Pricing | Trial ₹3,000 | Registry Feed";
  const description = "Simple transparent pricing — MCA database ₹21,999/month, GST database ₹29,999/month, 3-day trial ₹3,000. No lock-in. Pan-India coverage.";
  const keywords = "registry feed pricing, mca database price india, gst database price, buy mca database, buy gst company list";
  const bc = [{ name: "Home", path: "/" }, { name: "Pricing", path }];
  const jsonLd = [
    ...pageSchema({ title, description, path, breadcrumbs: bc }),
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "MCA Database — Monthly",
      description: "Daily updated MCA newly registered company database, pan-India",
      brand: { "@type": "Brand", name: "Registry Feed" },
      offers: { "@type": "Offer", price: "21999", priceCurrency: "INR", availability: "https://schema.org/InStock", url: "https://registryfeed.com/pricing" },
    },
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "GST Database — Monthly",
      description: "Daily updated GST newly registered business database, pan-India",
      brand: { "@type": "Brand", name: "Registry Feed" },
      offers: { "@type": "Offer", price: "29999", priceCurrency: "INR", availability: "https://schema.org/InStock", url: "https://registryfeed.com/pricing" },
    },
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "3-Day Trial",
      description: "3-day trial access to Registry Feed daily MCA and GST newly registered company data",
      brand: { "@type": "Brand", name: "Registry Feed" },
      offers: { "@type": "Offer", price: "3000", priceCurrency: "INR", availability: "https://schema.org/InStock", url: "https://registryfeed.com/pricing" },
    },
  ];
  return (
    <PublicPageLayout breadcrumbs={bc}>
      <Seo title={title} description={description} keywords={keywords} path={path} jsonLd={jsonLd} />
      <section className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14 sm:py-20">
        <div className="max-w-3xl">
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight">Registry Feed Pricing — Simple &amp; Transparent</h1>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">Pay per data stream, per month. Try our data with a 3-day Pan India trial. State-wise pricing is customised — talk to us on WhatsApp.</p>
        </div>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Tier
            label="Trial (Pan India, 3 days)"
            price={`₹${PRICING.trial.price.toLocaleString("en-IN")}`}
            period="3 days"
            features={["Full Pan India access", "GST or MCA (single stream)", "Daily files for 3 days", "See data quality first"]}
            wa={WA_MESSAGES.sample}
            testid="tier-trial"
          />
          <Tier
            label="MCA Company Database"
            price={`₹${PRICING.mca.price.toLocaleString("en-IN")}`}
            period="month"
            features={["Pan India MCA data", "Daily uploads for 30 days", "CSV downloads", "State / district filters"]}
            wa={WA_MESSAGES.mca}
            highlighted
            testid="tier-mca"
          />
          <Tier
            label="GST Company Database"
            price={`₹${PRICING.gst.price.toLocaleString("en-IN")}`}
            period="month"
            features={["Pan India GST data", "Daily uploads for 30 days", "CSV downloads", "State / city filters"]}
            wa={WA_MESSAGES.gst}
            highlighted
            testid="tier-gst"
          />
          <Tier
            label="State-wise Package"
            price="Custom"
            period="quote"
            features={["Single state or multi-state", "Choose GST, MCA or both", "Pay for what you need", "WhatsApp us your requirement"]}
            wa={WA_MESSAGES.general}
            testid="tier-state"
          />
        </div>

        <div className="mt-10 rounded-lg border border-border bg-muted/30 p-5 text-sm text-muted-foreground">
          <p><strong className="text-foreground">MCA + GST combined package</strong> is also available at a customised price. WhatsApp us at <span className="num text-foreground">+91 85379 42196</span> and we'll send a quote.</p>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <WhatsAppButton message={WA_MESSAGES.general} label="Talk to Sales on WhatsApp" testid="pricing-cta-wa" />
          <Link to="/sample-data"><Button variant="outline">Request Sample <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
        </div>
      </section>
    </PublicPageLayout>
  );
}
